import 'froala-editor/css/froala_style.min.css';
import 'froala-editor/css/froala_editor.pkgd.min.css';
import 'froala-editor/js/plugins.pkgd.min.js';
import React, {ReactNode, useEffect, useMemo, useRef, useState} from 'react';
import FroalaEditorView from 'react-froala-wysiwyg/FroalaEditorView';
import classes from '../../../styling/preview/css/main.module.css';
import '../../../styling/preview/css/froala_styles.css';
import Lottie, {LottieRefCurrentProps} from "lottie-react";
import successAnimation from '../../../styling/preview/Lottie/Success_Animation.json';
import ReactJoyride, {ACTIONS, CallBackProps, EVENTS, STATUS} from 'react-joyride';
import {LearningPageInfo} from "../../../model/LearningPageInfo";
import { darkenHexColor, darkenRgbColor, getInitials, getRelativeLuminance } from '../../../util/LearningPageHelpers';
import look2xLogo from '../../../styling/preview/img/look2x-logo.png';
import look2xLogoWeiss from '../../../styling/preview/img/look2x-logo-farbig-weiss.png';
import closeWindowIcon from '../../../styling/preview/img/close-window.svg';
import maximizeWindowIcon from '../../../styling/preview/img/maximize-window.svg';
import minimizeWindowIcon from '../../../styling/preview/img/minimize-window.svg';
import { useTranslation } from 'react-i18next';
interface Props {
  learningPageInfo: LearningPageInfo; 
  logo: Blob | null;
}

export const EmailPreview = (props: Props) => {

  const { learningPageInfo, logo: customerLogo } = props;
  const { filledSenderAddress, filledSubject, filledBody, learningContents, primaryColor, brandingEnabled } = learningPageInfo;
  const [mailOpen, setMailOpen] = useState<boolean>(false);
  const { i18n } = useTranslation();
  const t = i18n.getFixedT(learningPageInfo.language);

  // Centralized CSS custom properties calculation
  const cssCustomProperties = useMemo((): React.CSSProperties => {
    let primaryColorValue = 'var(--look2x-green)'; // Default
    let hoverColorValue = 'var(--look2x-green)'; // Default
    let textColorValue = 'var(--look2x-blue)'; // Default

    if (brandingEnabled && primaryColor && primaryColor !== '') {
      // Set primary color
      primaryColorValue = primaryColor;
      
      // Calculate hover color
      const isHex = /^#/.test(primaryColor);
      const darkenAmount = 40;
      hoverColorValue = isHex
        ? darkenHexColor(primaryColor, darkenAmount)
        : darkenRgbColor(primaryColor, darkenAmount);
      
      // Calculate text color based on luminance
      const luminance = getRelativeLuminance(primaryColor);
      textColorValue = luminance > 0.5 ? '#000000' : '#ffffff';
    }

    return {
      '--primary-color': primaryColorValue,
      '--hover-color': hoverColorValue,
      '--text-color': textColorValue
    } as React.CSSProperties;
  }, [brandingEnabled, primaryColor]);

  const joyrideStyles = {
    options: {
      arrowColor: '#fff',
      backgroundColor: '#fff',
      primaryColor: '#0E2445',
      textColor: '#000000',
      zIndex: 1000,
    },
    buttonNext: {
      color: 'white',
    },
    buttonBack: {
      borderRadius: '4px',
      marginRight: '0.5rem',
      backgroundColor: '#0E2445',
      color: 'white',
    }
  }

  useEffect(() => {
    // Disable all <a> tags on page load
    const allLinks = document.querySelectorAll('div.mail a');
    allLinks.forEach(link => {
      link.addEventListener('click', e => {
        e.preventDefault();
      });
    });
  }, [filledBody]);


  useEffect(() => {
    const updatedSteps = learningContents.map((content, index) => {
      // Create a step object for each learning content
      return {
        target: '#' + content.location,
        title: content.title,
        content: content.content as ReactNode,
        //Set disableBeacon = true for first step
        ...(index === 0 && { disableBeacon: true }),
      };
    });
    // Update the steps state with the new array
    setJoyrideState((prevState: any) => ({
      ...prevState,
      steps: updatedSteps.concat([{
        target: '.guideMail',
        title: t('emailPreview.successTitle'),
        content: <div><Lottie animationData={successAnimation} loop={true} style={{height:'4rem'}} lottieRef={lottieRef}/>
        {t('emailPreview.successMessage')}
        </div>,
        disableBeacon: true,
      }]),
    }));
  }, [learningContents, t]);

  const toggleTour = () => {
    setJoyrideState((prevState: any) => ({
      ...prevState,
      run: true,
      // If button is clicked during tour, go to first step again
      steps: prevState.steps.map((step: any, index: number) => {
        if (prevState.stepIndex !== 0) {
          if (index === 0) {
            step = {
              ...step,
              disableBeacon: false,
            }
          }
        }
        return step;
      }),
      stepIndex: 0,
    }));
  };

  const [joyrideState, setJoyrideState] = useState<any>({
    run: false,
    steps: [],
    stepIndex: 0, // a controlled tour
  });

  const handleJoyrideCallback = (data: CallBackProps) => {
    const { action, index, status, type } = data;
    if (([EVENTS.STEP_AFTER, EVENTS.TARGET_NOT_FOUND] as string[]).includes(type)) {
      const nextStepIndex = index + (action === ACTIONS.PREV ? -1 : 1);
      setJoyrideState((prevState: any) => ({
        ...prevState,
        stepIndex: nextStepIndex,
      }));
    } else if (([STATUS.FINISHED, STATUS.SKIPPED] as string[]).includes(status)) {
      // Need to set our running state to false, so we can restart if we click start again.
      setJoyrideState((prevState: any) => ({
        ...prevState,
        stepIndex: 0,
        run: false,
      }));
    }
  };



  //Open Mail Animation
  const guideSectionRef = useRef<HTMLDivElement>(null);
  const openMail = () => {
    setMailOpen(true);
    setTimeout(() => {
      guideSectionRef.current!.scrollIntoView({ behavior: 'smooth' });
    }, 300);
    // Start the opacity animation
    setTimeout(() => {
      guideSectionRef.current!.style.opacity = "1";
    }, 500); // Delay to ensure scroll completes before animation starts
    setTimeout(() => {
      toggleTour();
    }, 1000);
  }

  const joyRideScrollOffset = 350;

  const lottieRef = useRef<LottieRefCurrentProps | null>(null);

  return (
    <div className={classes.container} style={cssCustomProperties}>
        <>
          <ReactJoyride steps={joyrideState.steps} continuous={true} showProgress={true} scrollOffset={joyRideScrollOffset} run={joyrideState.run} stepIndex={joyrideState.stepIndex} callback={handleJoyrideCallback} styles={joyrideStyles} locale={{ back: t('joyride.back'), close: t('joyride.close'), last: t('joyride.last'), next: t('joyride.next'), open: t('joyride.open'), skip: t('joyride.skip') }} />
          <header className={classes.header}>
            <div>
              <img className={classes.ownLogo} src={look2xLogo} alt="look2x" />
            </div>
            <div className={classes.headerLogoCustomer}>
              {customerLogo != null && brandingEnabled ? (
                customerLogo.type === 'image/svg+xml' ? (
                  // Render SVG directly
                  <object type="image/svg+xml" className={classes.customerLogo} data={URL.createObjectURL(customerLogo)} />
                ) : (
                  // Render PNG or JPG
                  <img src={URL.createObjectURL(customerLogo)} className={classes.customerLogo} alt="Logo" />
                )
              ) : null}
            </div>
          </header>
          <main>
            <section className={classes.introSection}>
              <div className={classes.introHeadline}>
                {t('emailPreview.notWhatYouWereLookingFor')}
              </div>
              <div className={classes.introSubtitle}>
                  <span dangerouslySetInnerHTML={{ __html: t('emailPreview.clickedPhishingLink') }}></span><br/>
                  {t('emailPreview.butNoWorry')}
              </div>
              <div className={classes.introContent}>   
                <div className={classes.introContentEnumeration}>
                  <div className={classes.introContentEnumerationItem}><img className={classes.checkmarkIcon}
                                                                          src={"/assets/img/checkmark.svg"}
                                                                          height="20"/> {t('emailPreview.yourDataIsSafe')} </div>
                  <div className={classes.introContentEnumerationItem}><img className={classes.checkmarkIcon}
                                                                          src={"/assets/img/checkmark.svg"}
                                                                          height="20"/> {t('emailPreview.noNegativeConsequences')} </div>
                  <div className={classes.introContentEnumerationItem}><img className={classes.checkmarkIcon}
                                                                          src={"/assets/img/checkmark.svg"}
                                                                          height="20"/> {t('emailPreview.employerNotInformed')} </div>
                </div>
              </div>
              <div className={classes.introContentOpenNow}>
                  {t('emailPreview.learnIn2Minutes')}
              </div>
              {!mailOpen && (
                <button className={classes.introContentMailButton} onClick={openMail}>
                  {t('emailPreview.startGuide')}
                </button>
              )}
            </section>
            <section className={classes.guideSection} ref={guideSectionRef}>
              <div className={`${classes.guideMail} guideMail`}>
                <div className={classes.mailHeaderBar}>
                  <div className={classes.mailHeaderBarIcon}>
                    <object type="image/svg+xml" data={minimizeWindowIcon} width="16" height="16" />
                  </div>
                  <div className={classes.mailHeaderBarIcon}>
                    <object type="image/svg+xml" data={maximizeWindowIcon} width="16" height="16" />
                  </div>
                  <div className={classes.mailHeaderBarIcon}>
                    <object type="image/svg+xml" data={closeWindowIcon} width="16" height="16" />
                  </div>
                </div>
                <div className={classes.mailHeaderInfo}>
                  <div className={classes.mailHeaderSubject}>
                    <span id="l_subject"><strong>{filledSubject}</strong> </span>
                  </div>
                  <div className={classes.senderReceiverContainer}>
                    <div className={classes.senderIcon}>{getInitials(filledSenderAddress)}</div>
                    <div>
                      <div className={classes.mailHeaderSender}>
                      <span id="l_sender"><strong>{filledSenderAddress}</strong> </span>
                    </div>
                    <div>
                      <strong>{t('emailPreview.to')}</strong> {t('emailPreview.yourEmailAddress')}
                    </div>
                    </div>
                    
                  </div>
                  
                </div>
                <div className={classes.mailBody}>
                  <FroalaEditorView
                    model={filledBody}
                  />
                </div>
              </div>
              <div className={classes.guideButton}>
                <button className={classes.startTourButton} onClick={toggleTour}>{joyrideState.run !== true ? t('emailPreview.showGuideAndTips') : t('emailPreview.restartGuide')}</button>
              </div>
            </section>
          </main>
          <footer className={classes.footer}>
          <div className={classes.headerLogo}>
              <img className={classes.ownLogo} src={look2xLogoWeiss} alt="look2x" />
            </div>
            <div className={classes.footerCopyright}>
              {t('emailPreview.copyright')}
            </div>
          </footer>
        </>
    </div>

  );
}

export default EmailPreview;